/*
 *
 * GlobalNavBar
 *
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { number, bool, func, object, string } from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import {
  Nav,
  NavDropdown,
  Navbar,
  Col,
  Badge,
  Button,
  Overlay,
  Popover,
} from "react-bootstrap";
import FontAwesome from "react-fontawesome";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fromJS, Map } from "immutable";
import debounce from "lodash/debounce";
import Cookies from "js-cookie";
import "!style-loader!css-loader!./styles.css";
import {
  RolesAccessLibrary,
  getAllPermissions,
} from "utils/rolesAccessFunctions";
import { developByCriteriaURL, teamsUrl } from "utils/env";
import VideoInterviewMerchandizingModal from "components/VideoInterviewMerchandizingModal";
import HelpAssistantModal from "components/HelpAssistantModal";
import { viMerchandizingUtmParams } from "utils/helperConstants";
import {
  logUserOut,
  checkIfUserHasDevelopAccess,
  checkIfUserHasPosthireAccess,
  getSubDomain,
} from "utils/helperFunctions";
import { triggerAppCuesEvent, getUserType } from "utils/appCuesFunctions";
import LogoImage from "resources/images/criteriaLogo_platformNav.png";
import LogoImageSmall from "resources/images/CriteriaLogo_Small.png";
import LogoInsight from "resources/images/CriteriaLogo_Criteria_White_Insight.png";
import {
  faExternalLink,
  faLockKeyhole as faLockKeyholeRegular,
} from "@fortawesome/pro-regular-svg-icons";
import { faLockKeyhole as faLockKeyholeSolid } from "@fortawesome/pro-solid-svg-icons";
import { trackVIMerchModalTriggered } from "utils/trackingFunctions";
import {
  loadSearchFilterOptions,
  resetSearchFilters,
  setRefreshPage,
  setUserInput,
  updateSearchFilters,
  updateFirstTeamsLogin,
  setShowVIMerchandisingModal,
  sendSupportBotQuestion,
  sendResponseFeedback,
  resetSupportBotResponse,
  trackPosthireLogin,
} from "containers/App/actions";
import {
  getIdpLogoutUrl,
  setCookiesForDevelopLogin,
} from "containers/App/helpers";
import UpgradeTooltipContent from "components/UpgradeTooltipContent";
import { SearchFilterModal } from "./AdvancedSearchFilters";
import {
  LogoImgBig,
  LogoImgSmall,
  NavItemText,
  HorizontalRuleMobile,
  NavHelpSettings,
  HelpSettingsButtonMobile,
  HelpSettingsButtonLarge,
} from "./NavbarComponents";
import { fetchIdpInfo } from "./helpers";
import { routesMap } from "./constants";
import NavbarSearch from "./NavbarSearch";
import ManageNavDropdown from "./ManageNavDropdown";

const GlobalNavBar = ({
  isLoggedIn,
  location,
  toggleHideOverflow,
  featureFlags,
  userData,
  roles,
  vhs,
  showVIMerchandisingModal,
  identityProviderMode,
  dispatch,
  supportBotResponse,
  trackResponseId,
  sendingBotQuestion,
  supportBotResponseError,
  onResetSearchFilters,
  onSetRefreshPage,
  onSetUserInput,
  onUpdateFirstTeamsLogin,
  onUpdateSearchFilters,
  onSetShowVIMerchandisingModal,
  onSendSupportBotQuestion,
  onSendResponseFeedback,
  onResetSupportBotResponse,
  onTrackPosthireLogin,
  currentSearchText,
  paidThisSession,
  searchFilters,
  searchFilterOptions,
  onLoadSearchFilters,
}) => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showHelpAssistantModal, setShowHelpAssistantModal] = useState(false);
  const [showContentTooltip, setShowContentTooltip] = useState(false);
  const [showTemplateTooltip, setShowTemplateTooltip] = useState(false);

  // Create refs for tooltip targets at the top level of the component
  const contentTooltipRef = useRef(null);
  const templateTooltipRef = useRef(null);

  // Keep track of the active tooltip target
  const [activeTooltipTarget, setActiveTooltipTarget] = useState(null);

  // Function to determine tooltip placement based on screen width and target
  const getTooltipPlacement = (targetRef = null) => {
    // Check if we're dealing with content or template tooltips
    const isContentTooltip = targetRef === contentTooltipRef?.current;
    const isTemplateTooltip = targetRef === templateTooltipRef?.current;

    // For screens between 574px and 950px, use left placement
    if (
      !(isContentTooltip || isTemplateTooltip) &&
      window.innerWidth >= 574 &&
      window.innerWidth <= 950
    ) {
      return "left";
    }

    if (window.innerWidth < 574) {
      return "bottom";
    }
    // Default placement for other screen sizes
    return "right";
  };

  // State to track current tooltip placement
  const [tooltipPlacement, setTooltipPlacement] = useState(
    getTooltipPlacement(null) // No specific target for default state
  );

  // Update tooltip placement when window resizes
  useEffect(() => {
    const handleResizeTooltip = () => {
      setTooltipPlacement(getTooltipPlacement(activeTooltipTarget));
    };

    window.addEventListener("resize", handleResizeTooltip);
    return () => {
      window.removeEventListener("resize", handleResizeTooltip);
    };
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (showHelpAssistantModal) {
      const handlePathChange = () => {
        handleCloseHelpAssistantModal();
      };

      return handlePathChange;
    }
  }, [location.pathname]);

  const handleResize = useCallback(
    debounce(() => {
      if (window.innerWidth > 767) {
        setSearchFocused(false);
      }
    }, 450),
    []
  );

  const handleCloseHelpAssistantModal = () => {
    onResetSupportBotResponse();
    setShowHelpAssistantModal(false);
  };

  /* ---------- Event Handlers ----------- */

  const handleNavigationClick = (event) => {
    // Prevent the default anchor click behavior
    event.preventDefault();

    // Start with the current target
    let { target } = event;

    // Traverse up the DOM tree until you find an element with a `data-id` attribute
    while (!target?.dataset?.id) {
      target = target.parentElement;
    }

    // If no target is found, return
    if (!target?.dataset?.id) {
      return;
    }

    onResetSearchFilters();
    dispatch(push(`/${target.dataset.id}`));
    toggleNavbar(false);
  };

  const handleManageClick = (event) => {
    onResetSearchFilters();

    // directs AU locale users to localized help/faqs/supporting documents
    const isAU = userData?.locale === "AU";
    const isHelpNavEvent = [
      "helpCenterAssistant",
      "howToGuides",
      "faq",
      "resources",
      "testPortfolio",
      "updates",
    ].includes(event);

    if (isHelpNavEvent) {
      let url;

      switch (event) {
        case "helpCenterAssistant":
          setShowHelpAssistantModal(true);
          return;
        case "howToGuides": {
          if (userData?.partnerAccountId !== 47) {
            url = isAU
              ? "https://criteriacorp.helpdocs.io/category/qg9ubd74nb-how-to-guides"
              : "https://criteria.helpdocs.io/category/j204q0726l-how-to-guides";
          } else {
            url = isAU
              ? "https://app-au.criteriacorp.com"
              : "https://hireselect.criteriacorp.com";
          }
          break;
        }
        case "faq": {
          url = isAU
            ? "https://criteriacorp.helpdocs.io/category/shth5smm9y-faq"
            : "https://criteria.helpdocs.io/category/sqs0z7wbz4-faq";
          break;
        }
        case "resources": {
          url = isAU
            ? "https://criteriacorp.helpdocs.io/category/d9t27rj71n-resources"
            : "https://criteria.helpdocs.io/category/71m2d4mp9p-resources";
          break;
        }
        case "testPortfolio": {
          url = isAU
            ? "https://criteriacorp.helpdocs.io/category/1n2j52n2r7-test-portfolio"
            : "https://criteria.helpdocs.io/category/udtfyv2oax-test-portfolio";
          break;
        }
        case "updates": {
          url = isAU
            ? "https://criteriacorp.helpdocs.io/category/3m27rjdwvi-updates"
            : "https://criteria.helpdocs.io/category/hbgwyfyhzq-updates";
          break;
        }
        default:
          dispatch(push(`/${event}`));
          break;
      }

      window.open(url);
    } else {
      dispatch(push(`/${event}`));
    }
    toggleNavbar(false);
  };

  const handleLogoutClick = () => {
    const emailAddress = userData?.emailAddress;

    if (!Cookies.get().ssoLoginSuccess) {
      localStorage.removeItem("pTS");
      // Stop the ChurnZero integration
      window.ChurnZero.push(["stop"]);

      if (identityProviderMode > 0) {
        sessionStorage.setItem("ssoLogout", true);
      }

      logUserOut();
      return;
    }

    fetchIdpInfo(emailAddress).then((idpInfo) => {
      if (identityProviderMode > 0) {
        sessionStorage.setItem("ssoLogout", true);
      }

      localStorage.removeItem("token");

      Cookies.remove("critToken", {
        path: "/",
        domain:
          new URL(teamsUrl).hostname === "localhost"
            ? "localhost"
            : ".criteriacorp.com",
      });

      if (localStorage.getItem("pTS")) {
        localStorage.removeItem("pTS");
      }

      window.ChurnZero.push(["stop"]);

      Cookies.remove("ssoLoginSuccess");
      const logoutUrl = getIdpLogoutUrl(
        idpInfo,
        "root"
        // sessionStorage.getItem('loginType')
      );
      window.location = logoutUrl;
    });
  };

  const handleAdvancedClick = () => {
    setShowFilterModal(true);
    onLoadSearchFilters();
  };

  const hideSearchModal = () => {
    setShowFilterModal(false);
  };

  const handleFilteredSearchSubmit = (formData) => {
    focusSearch(false);
    onResetSearchFilters();
    onUpdateSearchFilters(formData);
    hideSearchModal();
    toggleNavbar(false);

    if (window.location.href.indexOf("/search") > -1) {
      onSetRefreshPage();
    } else {
      dispatch(push("/search"));
    }
  };

  const toggleNavbar = (expandedBool) => {
    if (expandedBool !== true && expandedBool !== false) {
      setNavbarExpanded((prevExpanded) => !prevExpanded);
      toggleHideOverflow(!navbarExpanded);
    } else {
      setNavbarExpanded(expandedBool);
      toggleHideOverflow(expandedBool);
    }
  };

  const focusSearch = (newSearchFocused) => {
    setSearchFocused((prev) =>
      typeof newSearchFocused === "boolean" ? newSearchFocused : !prev
    );
  };

  const handleTeamsButton = async (source = false) => {
    const token = localStorage.getItem("token");
    // although the critToken cookie should already exist after login, we need to set it again
    // in case the user logged into another Criteria instance in another window.
    if (!userData?.firstTeamsLogin) {
      onUpdateFirstTeamsLogin();
    }

    const hasDevelopEnabled = checkIfUserHasDevelopAccess(userData);

    if (hasDevelopEnabled) {
      setCookiesForDevelopLogin(token);
      await onTrackPosthireLogin();
      localStorage.removeItem("token");
      window.location.href = developByCriteriaURL;
      return;
    }

    if (!hasDevelopEnabled && source) {
      const localeInstance = getSubDomain() === "app-au" ? "AU" : "NA";
      const isAutoRenewalRep =
        rep?.am?.userId === 304 || rep?.am?.userId === 305;
      const isFreeTrial = userData?.status === "free_trial";

      const userType = getUserType(
        isFreeTrial,
        isVideoInterviewEnabled,
        isAutoRenewalRep,
        true
      );

      const eventTriggerAttributes = {
        surface_area: source,
        user_type: userType,
        instance: localeInstance,
      };

      triggerAppCuesEvent(
        "develop_modal",
        eventTriggerAttributes,
        "https://www.criteriacorp.com/develop",
        userData?.userAccountId
      );
    }

    if (checkIfUserHasPosthireAccess(userData)) {
      window.analytics.track(
        "talent insights login",
        {
          "referral source type": "platform button",
          url: teamsUrl,
        },
        () => {
          localStorage.removeItem("token");
          window.location.href = teamsUrl;
        }
      );
    }
  };

  const handleShowVIMerchandizingModal = () => {
    trackVIMerchModalTriggered("vi-manage-templates", 1, "VI");

    onSetShowVIMerchandisingModal(viMerchandizingUtmParams.manageVITemplates);
  };

  const handleCloseVIMerchandizingModal = () => {
    onSetShowVIMerchandisingModal(null);
  };

  const handleSendSupportBotQuestion = (questionText) => {
    const { companyAccountId, userAccountId } = userData;
    onSendSupportBotQuestion({
      questionText,
      companyAccountId,
      platformUserId: userAccountId,
    });
  };

  const handleSendResponseFeedback = (responseQuality) => {
    onSendResponseFeedback({
      responseId: Number(trackResponseId),
      quality: Number(responseQuality),
    });
  };

  const getDevelopNavButtonText = (
    { whiteIcon, hasPostHirePlatformAccess } = {
      whiteIcon: false,
      hasPostHirePlatformAccess: false,
    }
  ) => (
    <NavItemText
      id="nav-dashboard"
      className="d-flex align-items-center h-100 m-0"
      style={{ gap: "8px" }}
    >
      Develop
      {whiteIcon && (
        <FontAwesomeIcon
          icon={hasPostHirePlatformAccess ? faExternalLink : faLockKeyholeSolid}
          className={`teams-external-link-icon ${whiteIcon ? "white" : ""}`}
        />
      )}
    </NavItemText>
  );

  const handleSearchInput = (searchInput) => {
    onSetUserInput(["currentSearchText"], searchInput);
    onSetUserInput(["searchFilters", "searchText"], searchInput);
  };

  const submitSearchInput = (searchInput) => {
    onResetSearchFilters();
    onSetUserInput(["searchFilters", "searchText"], searchInput);
    toggleNavbar(false);
    if (window.location.href.indexOf("/search") > -1) {
      onSetRefreshPage();
    } else {
      dispatch(push("/search"));
    }
  };

  const showProUpgrades = vhs === 1;
  const showStandardUpgrades = vhs < 1;
  const isTSS = userData?.hsVersion === 5;
  const isAdmin = userData?.roleId === 1;
  const isInsightUser = userData?.partnerAccountId === 47;
  const isVideoInterviewEnabled = userData?.videoInterviewsEnabled;
  const rep = userData?.rep;
  const isFreeTrial = userData?.status === "free_trial";
  const isAU = userData?.locale === "AU";

  const parsedFeatureFlags = Map.isMap(featureFlags)
    ? featureFlags
    : fromJS(featureFlags);

  const hasPostHirePlatformAccess = checkIfUserHasPosthireAccess(userData);
  const hasDevelopAccess = checkIfUserHasDevelopAccess(userData);
  let showDevelopButton =
    hasDevelopAccess || (!hasDevelopAccess && !hasPostHirePlatformAccess);
  let showTalentInsightsButton = !showDevelopButton;

  // We want to hide the posthire button if the user does not have access to posthire and not admin since they won't be able to access it or ask for it
  if (!userData?.tmgAccess && !isAdmin) {
    showDevelopButton = false;
    showTalentInsightsButton = false;
  }

  const RolesAccessFuncLibrary =
    roles.size > 0 && userData
      ? RolesAccessLibrary(roles, userData.roleId)
      : null;

  const permissionsObj = RolesAccessFuncLibrary
    ? getAllPermissions(RolesAccessFuncLibrary)
    : {};

  const {
    jobsPermission,
    resultsPermission,
    analyticsPermission,
    batteriesPermission,
    baselinesPermission,
    usersTeamsPermission,
    accessRolesPermission,
    companyPrefPermission,
    integrationsPermission,
    testMakerPermission,
    jobAppsPermission,
    pipelinesPermission,
    emailTemplatesPermission,
    ratingSystemsPermission,
    landingPagesPermission,
    exitPagesPermission,
    collaboratorOnlyPermission,
    interviewTemplatesPermission,
    competencySetsPermission,
  } = permissionsObj;

  const noManageTab =
    !showProUpgrades &&
    !showStandardUpgrades &&
    !batteriesPermission &&
    !baselinesPermission &&
    !analyticsPermission &&
    !testMakerPermission &&
    !jobAppsPermission &&
    !pipelinesPermission &&
    !ratingSystemsPermission &&
    !emailTemplatesPermission &&
    !landingPagesPermission &&
    !exitPagesPermission &&
    !interviewTemplatesPermission &&
    !collaboratorOnlyPermission &&
    !competencySetsPermission;

  const activeNavItem = location?.pathname
    ? routesMap[location.pathname.toLowerCase()]
    : "dashboard";

  // Rendering helper functions
  const renderUpgradeOrItem = (
    permission,
    itemProps,
    featureName,
    feature = null,
    showTooltip = false,
    tooltipProps = {}
  ) => {
    if (!permission) {
      return null;
    }

    const showMerchandizingLock =
      !isInsightUser && showProUpgrades && showTooltip;

    const merchandizingLock = (
      <FontAwesomeIcon
        className="mr-2"
        icon={faLockKeyholeRegular}
        style={{ fontSize: "16px" }}
      />
    );

    const navItem = (
      <NavDropdown.Item
        {...itemProps}
        disabled={showMerchandizingLock}
        className={showMerchandizingLock ? "disabled-item" : ""}
      >
        {showMerchandizingLock && merchandizingLock}
        {featureName}
      </NavDropdown.Item>
    );

    if (showTooltip && showMerchandizingLock) {
      const { show, onMouseEnter, onMouseLeave } = tooltipProps;

      // Custom handlers to set the active tooltip target
      const handleMouseEnter = (e) => {
        setActiveTooltipTarget(e.currentTarget);
        if (onMouseEnter) {
          onMouseEnter(e);
        }
      };

      const handleMouseLeave = (e) => {
        if (onMouseLeave) {
          onMouseLeave(e);
        }
      };

      return (
        <>
          <div
            className="dropdown-item-wrapper"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {navItem}
          </div>

          <Overlay
            show={show}
            placement={tooltipPlacement}
            target={activeTooltipTarget}
            popperConfig={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: tooltipPlacement === "bottom" ? [0, 4] : [0, 8],
                  },
                },
              ],
            }}
          >
            <Popover
              className="upgrade-tooltip"
              id="tooltip-overlay"
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              <UpgradeTooltipContent
                onMouseLeave={onMouseLeave}
                isFreeTrial={isFreeTrial}
                userData={userData}
                feature={feature}
                utmContent="free_trial_se"
                utmCampaign="manage_menu"
                source="manage_menu"
                dispatch={dispatch}
              />
            </Popover>
          </Overlay>
        </>
      );
    }

    return navItem;
  };

  const handleContentTooltipEnter = () => {
    setShowContentTooltip(true);
  };

  const handleContentTooltipLeave = () => {
    setShowContentTooltip(false);
  };

  const handleTemplateTooltipEnter = () => {
    setShowTemplateTooltip(true);
  };

  const handleTemplateTooltipLeave = () => {
    setShowTemplateTooltip(false);
  };

  const getStructuredInterviewItems = () => {
    if (!interviewTemplatesPermission) {
      return null;
    }

    // SI Merchandizing will not be shown for Insight partner
    const showSIMerchandizingLock = !isInsightUser && !isVideoInterviewEnabled;

    const onSelectHandler = showSIMerchandizingLock
      ? handleShowVIMerchandizingModal
      : handleManageClick;

    const merchandizingLock = (
      <FontAwesomeIcon
        className="mr-2"
        icon={faLockKeyholeRegular}
        style={{ fontSize: "16px" }}
      />
    );

    if (showSIMerchandizingLock) {
      return (
        <>
          <div
            ref={contentTooltipRef}
            className="dropdown-item-wrapper"
            onMouseEnter={handleContentTooltipEnter}
            onMouseLeave={handleContentTooltipLeave}
          >
            <NavDropdown.Item
              eventKey="contentLibrary"
              onSelect={onSelectHandler}
              id="nav-manage-content-library"
              disabled={showSIMerchandizingLock}
              className={showSIMerchandizingLock ? "disabled-item" : null}
            >
              {showSIMerchandizingLock && merchandizingLock}
              Interview Content Library
            </NavDropdown.Item>
          </div>

          <Overlay
            show={showContentTooltip}
            placement={getTooltipPlacement(contentTooltipRef.current)}
            target={contentTooltipRef}
            popperConfig={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: tooltipPlacement === "bottom" ? [0, 4] : [0, 8],
                  },
                },
              ],
            }}
          >
            <Popover
              className="upgrade-tooltip"
              id="tooltip-overlay"
              onMouseEnter={handleContentTooltipEnter}
              onMouseLeave={handleContentTooltipLeave}
            >
              <UpgradeTooltipContent
                onMouseLeave={handleContentTooltipLeave}
                isFreeTrial={isFreeTrial}
                userData={userData}
                feature="structuredInterviewing"
                utmContent="free_trial_se"
                utmCampaign="manage_menu"
                source="manage_menu"
                dispatch={dispatch}
              />
            </Popover>
          </Overlay>
          <div
            ref={templateTooltipRef}
            className="dropdown-item-wrapper"
            onMouseEnter={handleTemplateTooltipEnter}
            onMouseLeave={handleTemplateTooltipLeave}
          >
            <NavDropdown.Item
              eventKey="interviewTemplates"
              onSelect={onSelectHandler}
              id="nav-manage-interview-templates"
              disabled={showSIMerchandizingLock}
              className={showSIMerchandizingLock ? "disabled-item" : null}
            >
              {showSIMerchandizingLock && merchandizingLock}
              Interview Templates
            </NavDropdown.Item>
          </div>

          <Overlay
            show={showTemplateTooltip}
            placement={getTooltipPlacement(templateTooltipRef.current)}
            target={templateTooltipRef}
            popperConfig={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: tooltipPlacement === "bottom" ? [0, 4] : [0, 8],
                  },
                },
              ],
            }}
          >
            <Popover
              className="upgrade-tooltip"
              id="tooltip-overlay"
              onMouseEnter={handleTemplateTooltipEnter}
              onMouseLeave={handleTemplateTooltipLeave}
            >
              <UpgradeTooltipContent
                onMouseLeave={handleTemplateTooltipLeave}
                isFreeTrial={isFreeTrial}
                userData={userData}
                feature="structuredInterviewing"
                utmContent="free_trial_se"
                utmCampaign="manage_menu"
                source="manage_menu"
                dispatch={dispatch}
              />
            </Popover>
          </Overlay>
        </>
      );
    }

    return (
      <>
        <NavDropdown.Item
          eventKey="contentLibrary"
          onSelect={onSelectHandler}
          id="nav-manage-content-library"
          disabled={showSIMerchandizingLock}
          className={showSIMerchandizingLock ? "disabled-item" : null}
        >
          {showSIMerchandizingLock && merchandizingLock}
          Interview Content Library
        </NavDropdown.Item>
        <NavDropdown.Item
          eventKey="interviewTemplates"
          onSelect={onSelectHandler}
          id="nav-manage-interview-templates"
          disabled={showSIMerchandizingLock}
          className={showSIMerchandizingLock ? "disabled-item" : null}
        >
          {showSIMerchandizingLock && merchandizingLock}
          Interview Templates
        </NavDropdown.Item>
      </>
    );
  };

  return (
    <div>
      <Navbar
        variant="dark"
        className="global-navbar"
        expand="md"
        onToggle={toggleNavbar}
        expanded={navbarExpanded}
      >
        <a href="/dashboard" className="ml-3 mr-auto">
          <LogoImgBig
            src={LogoImage}
            alt="Criteria Logo"
            width="96"
            height="25.5"
          />
          {isInsightUser ? (
            <LogoImgBig
              src={LogoInsight}
              alt="Insight Logo"
              width="84"
              height="25.5"
              className="ml-4"
            />
          ) : null}
          <LogoImgSmall
            src={LogoImageSmall}
            alt="Criteria Logo"
            width="30"
            height="30"
            className="text-center"
          />
        </a>
        {resultsPermission ? (
          <NavbarSearch
            searchFocused={searchFocused}
            handleSubmit={submitSearchInput}
            currentSearchText={currentSearchText}
            handleSearchInput={handleSearchInput}
            onHandleAdvancedClick={handleAdvancedClick}
            setSearchFocus={focusSearch}
            mobileWidth={1200}
            disappearingMaxWidth={767}
            disappearingMinWidth={0}
            className="navbar-search-mobile"
          />
        ) : null}
        <Navbar.Toggle />
        {userData &&
        (userData.daysRemaining > 0 ||
          paidThisSession ||
          userData.internal !== 0) ? (
          // eslint-disable-next-line react/jsx-indent
          <Navbar.Collapse>
            <Nav
              className="ml-3 mr-auto"
              style={{
                display: searchFocused ? "none" : "",
              }}
            >
              <Nav.Link
                onClick={handleNavigationClick}
                data-id="dashboard"
                id="dashboard"
                className={activeNavItem === "dashboard" ? "active" : ""}
                style={{ fontSize: "15px" }}
              >
                <NavItemText data-id="dashboard" id="nav-dashboard">
                  Dashboard
                </NavItemText>
              </Nav.Link>
              {!collaboratorOnlyPermission ? (
                <Nav.Link
                  onClick={handleNavigationClick}
                  data-id="jobs"
                  id="jobs"
                  className={activeNavItem === "jobs" ? "active" : ""}
                  style={{ fontSize: "15px" }}
                  disabled={jobsPermission === undefined}
                >
                  <NavItemText data-id="jobs" id="nav-jobs">
                    Jobs
                  </NavItemText>
                </Nav.Link>
              ) : null}
              {!collaboratorOnlyPermission ? (
                <Nav.Link
                  onClick={handleNavigationClick}
                  data-id="resultsLanding"
                  id="resultsLanding"
                  className={activeNavItem === "results" ? "active" : ""}
                  style={{ fontSize: "15px" }}
                  disabled={resultsPermission === undefined}
                >
                  <NavItemText data-id="resultsLanding" id="nav-results">
                    Results
                  </NavItemText>
                </Nav.Link>
              ) : null}
              {parsedFeatureFlags.get("isAnalyticsEnabled") &&
              !collaboratorOnlyPermission &&
              !(isInsightUser && !isAdmin) ? (
                <Nav.Link
                  onClick={handleNavigationClick}
                  data-id="analytics"
                  id="analytics"
                  className={activeNavItem === "analytics" ? "active" : ""}
                  style={{ fontSize: "15px" }}
                  disabled={analyticsPermission === undefined}
                >
                  <NavItemText data-id="analytics" id="nav-analytics">
                    Analytics
                  </NavItemText>
                </Nav.Link>
              ) : null}
              {showTalentInsightsButton ? (
                <Nav.Link
                  onClick={handleTeamsButton}
                  data-id="teams"
                  id="teams"
                  className="teams-link"
                  style={{ fontSize: "15px" }}
                >
                  <NavItemText data-id="teams" id="nav-dashboard">
                    Talent Insights
                  </NavItemText>
                </Nav.Link>
              ) : null}
              <HorizontalRuleMobile />
              <ManageNavDropdown
                activeNavItem={activeNavItem}
                noManageTab={noManageTab}
                handleManageClick={handleManageClick}
                getStructuredInterviewItems={getStructuredInterviewItems}
                renderUpgradeOrItem={renderUpgradeOrItem}
                batteriesPermission={batteriesPermission}
                baselinesPermission={baselinesPermission}
                analyticsPermission={analyticsPermission}
                testMakerPermission={testMakerPermission}
                collaboratorOnlyPermission={collaboratorOnlyPermission}
                jobAppsPermission={jobAppsPermission}
                pipelinesPermission={pipelinesPermission}
                landingPagesPermission={landingPagesPermission}
                exitPagesPermission={exitPagesPermission}
                ratingSystemsPermission={ratingSystemsPermission}
                emailTemplatesPermission={emailTemplatesPermission}
                isInsightUser={isInsightUser}
                showProUpgrades={showProUpgrades}
                isFreeTrial={isFreeTrial}
                isTSS={isTSS}
                userData={userData}
              />
              <HorizontalRuleMobile />
              {showDevelopButton ? (
                <>
                  <Nav.Link
                    onClick={() => handleTeamsButton("nav_bar")}
                    style={{ fontSize: "15px" }}
                    className={`post-hire-link ${hasPostHirePlatformAccess ? "" : "locked"}`}
                    id={
                      hasPostHirePlatformAccess
                        ? "develop-nav-link"
                        : "develop-nav-link-locked"
                    }
                  >
                    {getDevelopNavButtonText({
                      whiteIcon: true,
                      hasPostHirePlatformAccess,
                    })}
                  </Nav.Link>
                  <HorizontalRuleMobile />
                </>
              ) : null}
            </Nav>

            {resultsPermission ? (
              <Col>
                <NavbarSearch
                  searchFocused={searchFocused}
                  handleSubmit={submitSearchInput}
                  currentSearchText={currentSearchText}
                  handleSearchInput={handleSearchInput}
                  onHandleAdvancedClick={handleAdvancedClick}
                  setSearchFocus={focusSearch}
                  mobileWidth={1200}
                  disappearingMinWidth={768}
                  className="navbar-search-desktop"
                />
              </Col>
            ) : null}
            <Nav>
              {showTalentInsightsButton ? (
                <HelpSettingsButtonLarge>
                  <Button
                    variant="outline-light"
                    className="teaming-button mr-3 mt-1 text-nowrap"
                    onClick={handleTeamsButton}
                  >
                    Talent Insights
                  </Button>
                </HelpSettingsButtonLarge>
              ) : null}
              {!isInsightUser ? (
                <NavDropdown
                  style={{ margin: "0" }}
                  title={
                    <span>
                      <HelpSettingsButtonMobile>Help</HelpSettingsButtonMobile>
                      <HelpSettingsButtonLarge>
                        <FontAwesome
                          name="question-circle-o"
                          style={{ fontSize: "21px" }}
                        />
                      </HelpSettingsButtonLarge>
                    </span>
                  }
                  id="help_dropdown"
                  alignRight
                  className={`${
                    activeNavItem === "help" ? "active" : ""
                  } basic-nav-dropdown no-padding-sm`}
                >
                  {parsedFeatureFlags.get("isAISupportAssistantEnabled") ? (
                    <NavDropdown.Item
                      eventKey="helpCenterAssistant"
                      onSelect={handleManageClick}
                    >
                      Help Center Assistant
                      <Badge className="new-badge">Beta</Badge>
                    </NavDropdown.Item>
                  ) : null}
                  <NavDropdown.Item
                    eventKey="howToGuides"
                    onSelect={handleManageClick}
                  >
                    How-To Guides
                  </NavDropdown.Item>
                  <NavDropdown.Item eventKey="faq" onSelect={handleManageClick}>
                    FAQ
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    eventKey="resources"
                    onSelect={handleManageClick}
                  >
                    Resources
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    eventKey="testPortfolio"
                    onSelect={handleManageClick}
                  >
                    Test Portfolio
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    eventKey="updates"
                    onSelect={handleManageClick}
                  >
                    Criteria Updates
                  </NavDropdown.Item>
                </NavDropdown>
              ) : null}
              <HorizontalRuleMobile />
              <NavDropdown
                style={{ margin: "0" }}
                title={
                  <span>
                    <HelpSettingsButtonMobile>
                      Settings
                    </HelpSettingsButtonMobile>
                    <HelpSettingsButtonLarge>
                      <FontAwesome
                        name="user-circle"
                        style={{ fontSize: "21px" }}
                      />
                    </HelpSettingsButtonLarge>
                  </span>
                }
                id="basic-nav-dropdown"
                alignRight
                className={`${
                  activeNavItem === "settings" ? "active" : ""
                } basic-nav-dropdown no-padding-sm`}
              >
                <NavDropdown.Header
                  style={{ fontWeight: "bold" }}
                  className="no-display-mobile"
                >
                  {userData && `${userData.firstName} ${userData.lastName}`}
                </NavDropdown.Header>
                <NavDropdown.Divider className="no-display-mobile" />
                <NavDropdown.Item
                  eventKey="userPreferences"
                  onSelect={handleManageClick}
                >
                  User Preferences
                </NavDropdown.Item>
                {companyPrefPermission ? (
                  <NavDropdown.Item
                    eventKey="companyPreferences"
                    onSelect={handleManageClick}
                  >
                    Company Preferences
                  </NavDropdown.Item>
                ) : null}
                {usersTeamsPermission ? (
                  <NavDropdown.Item
                    eventKey="users"
                    onSelect={handleManageClick}
                  >
                    Users
                  </NavDropdown.Item>
                ) : null}
                {usersTeamsPermission ? (
                  <NavDropdown.Item
                    eventKey="teams"
                    onSelect={handleManageClick}
                  >
                    Teams
                  </NavDropdown.Item>
                ) : null}
                {accessRolesPermission ? (
                  <NavDropdown.Item
                    eventKey="accessRoles"
                    onSelect={handleManageClick}
                  >
                    Access Roles
                  </NavDropdown.Item>
                ) : null}
                {integrationsPermission ? (
                  <NavDropdown.Item
                    eventKey="integrations"
                    onSelect={handleManageClick}
                  >
                    Integrations
                  </NavDropdown.Item>
                ) : null}
                {showProUpgrades || showStandardUpgrades ? (
                  <NavDropdown.Item
                    eventKey="versionsInfo"
                    onSelect={handleManageClick}
                  >
                    Upgrade
                  </NavDropdown.Item>
                ) : null}
                {!isFreeTrial && isAdmin ? (
                  <NavDropdown.Item
                    eventKey="accountBillingDetails"
                    onSelect={handleManageClick}
                  >
                    Account Billing Details
                  </NavDropdown.Item>
                ) : null}
                {showDevelopButton ? (
                  <div className="dropdown-item-wrapper">
                    <NavDropdown.Item
                      eventKey="accountBillingDetails"
                      onSelect={() => handleTeamsButton("settings_dropdown")}
                      className={
                        !hasPostHirePlatformAccess
                          ? "d-flex align-items-center disabled-item"
                          : null
                      }
                    >
                      {!hasPostHirePlatformAccess && (
                        <FontAwesomeIcon
                          className="mr-2"
                          icon={faLockKeyholeRegular}
                          style={{ fontSize: "16px" }}
                        />
                      )}
                      {getDevelopNavButtonText({
                        hasPostHirePlatformAccess,
                      })}
                    </NavDropdown.Item>
                  </div>
                ) : null}
                <NavDropdown.Divider className="no-display-mobile" />
                {isLoggedIn && (
                  <NavDropdown.Item
                    eventKey="login"
                    onSelect={handleLogoutClick}
                    className="no-display-mobile"
                  >
                    Log Out{" "}
                    {userData && `${userData.firstName} ${userData.lastName}`}
                  </NavDropdown.Item>
                )}
              </NavDropdown>
              <HorizontalRuleMobile />
              <Nav.Link
                onClick={handleLogoutClick}
                className="only-display-mobile"
                style={{ fontSize: "13px" }}
              >
                <NavItemText>
                  Log Out{" "}
                  {userData && `${userData.firstName} ${userData.lastName}`}
                </NavItemText>
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        ) : (
          <NavHelpSettings>
            <Nav>
              <NavDropdown
                title={
                  <span>
                    <HelpSettingsButtonMobile>
                      Settings
                    </HelpSettingsButtonMobile>
                    <HelpSettingsButtonLarge>
                      <FontAwesome
                        name="user-circle"
                        style={{ fontSize: "21px" }}
                      />
                    </HelpSettingsButtonLarge>
                  </span>
                }
                className={`${activeNavItem === "settings" ? "active" : ""} basic-nav-dropdown`}
                alignRight
              >
                {isLoggedIn && (
                  <NavDropdown.Item
                    eventKey="login"
                    onSelect={handleLogoutClick}
                    className="no-display-mobile"
                  >
                    Log Out{" "}
                    {userData && `${userData.firstName} ${userData.lastName}`}
                  </NavDropdown.Item>
                )}
              </NavDropdown>
            </Nav>
          </NavHelpSettings>
        )}
      </Navbar>
      {resultsPermission && showFilterModal && (
        <SearchFilterModal
          searchFilterOptions={searchFilterOptions}
          hideSearchModal={hideSearchModal}
          handleFilteredSearchSubmit={handleFilteredSearchSubmit}
          searchFilters={searchFilters}
          currentSearchText={currentSearchText}
          onSetUserInput={onSetUserInput}
          onResetSearchFilters={onResetSearchFilters}
        />
      )}
      {showVIMerchandisingModal ? (
        <VideoInterviewMerchandizingModal
          showModal={!!showVIMerchandisingModal}
          handleCloseVIMerchandizingModal={handleCloseVIMerchandizingModal}
          utmParams={showVIMerchandisingModal}
          rep={rep}
        />
      ) : null}
      {showHelpAssistantModal ? (
        <HelpAssistantModal
          showModal={showHelpAssistantModal}
          handleCloseHelpAssistantModal={handleCloseHelpAssistantModal}
          handleSendSupportBotQuestion={handleSendSupportBotQuestion}
          handleSendResponseFeedback={handleSendResponseFeedback}
          supportBotResponse={supportBotResponse}
          isAU={isAU}
          responseId={trackResponseId}
          sendingBotQuestion={sendingBotQuestion}
          supportBotResponseError={supportBotResponseError}
        />
      ) : null}
    </div>
  );
};

GlobalNavBar.propTypes = {
  currentSearchText: string,
  dispatch: func.isRequired,
  identityProviderMode: number,
  isLoggedIn: bool.isRequired,
  onLoadSearchFilters: func.isRequired,
  onResetSearchFilters: func.isRequired,
  onSetRefreshPage: func.isRequired,
  onSetUserInput: func.isRequired,
  onUpdateFirstTeamsLogin: func.isRequired,
  onUpdateSearchFilters: func.isRequired,
  paidThisSession: bool,
  searchFilterOptions: object,
  searchFilters: object,
  toggleHideOverflow: func.isRequired,
  onSetShowVIMerchandisingModal: func.isRequired,
  roles: object,
  showVIMerchandisingModal: object,
  userData: object,
  vhs: number,
  featureFlags: object,
  location: object,
  supportBotResponse: string,
  trackResponseId: string,
  onSendSupportBotQuestion: func.isRequired,
  onSendResponseFeedback: func.isRequired,
  sendingBotQuestion: bool,
  supportBotResponseError: bool,
  onResetSupportBotResponse: func.isRequired,
  onTrackPosthireLogin: func,
};

const mapStateToProps = (state) => ({
  searchFilterOptions: state.get("App").get("searchFilterOptions"),
  paidThisSession: state.get("App").get("paidThisSession"),
  searchFilters: state.get("App").get("searchFilters"),
  currentSearchText: state.get("App").get("currentSearchText"),
  identityProviderMode: state.get("App").get("identityProviderMode"),
  roles: state.get("App").get("roles"),
  userData: state.get("App").get("userData"),
  vhs: state.get("App").get("vhs"),
  featureFlags: state.get("App").get("featureFlags"),
  showVIMerchandisingModal: state.get("App").get("showVIMerchandisingModal"),
  supportBotResponse: state.get("App").get("supportBotResponse"),
  trackResponseId: state.get("App").get("trackResponseId"),
  sendingBotQuestion: state.get("App").get("sendingBotQuestion"),
  supportBotResponseError: state.get("App").get("supportBotResponseError"),
});

function mapDispatchToProps(dispatch) {
  return {
    onSetUserInput: (field, value) => dispatch(setUserInput(field, value)),
    onResetSearchFilters: () => dispatch(resetSearchFilters()),
    onLoadSearchFilters: () => dispatch(loadSearchFilterOptions()),
    onUpdateSearchFilters: (formData) =>
      dispatch(updateSearchFilters(formData)),
    onSetRefreshPage: () => dispatch(setRefreshPage()),
    onUpdateFirstTeamsLogin: () => dispatch(updateFirstTeamsLogin()),
    onSetShowVIMerchandisingModal: (value) =>
      dispatch(setShowVIMerchandisingModal(value)),
    onSendSupportBotQuestion: (formData) =>
      dispatch(sendSupportBotQuestion(formData)),
    onSendResponseFeedback: (formData) =>
      dispatch(sendResponseFeedback(formData)),
    onResetSupportBotResponse: () => dispatch(resetSupportBotResponse()),
    onTrackPosthireLogin: () => dispatch(trackPosthireLogin()),
    dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(GlobalNavBar);
